import {
  ControlConfig,
  getSocketModeFromFlag,
  getDryingModeFromFlag,
  ComBoxSensorUsage,
} from "./ControlConfig";
import { IresDeviceResponseReader } from "@/utils/IresDeviceResponseReader";
import { IresDeviceFrameBuilder } from "@/utils/IresDeviceFrameBuilder";
import * as base64Buffer from "base64-arraybuffer";
import { filterOutUndefined, generateRandomUuid } from "@/utils/Utils";
import { ComBoxHumidityUnit } from "./ComBoxHumidityUnit";
import { hasCapabilityForVersion, DeviceCapability } from "./DeviceCapability";

export interface ComBoxClientConfig {
  name: string;
  version: number;
  displayMessage: string;
  expertModeEnabled: boolean;
  selectableDryingPrograms: boolean[];
  selectableDryingProgramsMini: boolean[];
  defaultDryingProgramMini: number;
  selectableSensorPrograms: boolean[];
  humidityUnit: ComBoxHumidityUnit;
  hiddenHumidityUnit?: ComBoxHumidityUnit;

  defaultControlConfig: ControlConfig;
}

/**
 * What a client without a saved client config gets: the defaults the client config dialog
 * ("Mandantenkonfiguration erstellen") suggests. The start / change dialog applies the same, so a
 * client sees the programs there that the config dialog shows it. The config dialog's
 * Standard Programm (Box) starts on DEFAULT_DRYING_PROGRAM_BOX as ControlConfig's initial program.
 */
export const DEFAULT_SELECTABLE_DRYING_PROGRAMS_BOX = [0, 1, 3, 4];
export const DEFAULT_DRYING_PROGRAM_BOX = 0;
export const DEFAULT_SELECTABLE_DRYING_PROGRAMS_MINI = [3, 4];
export const DEFAULT_DRYING_PROGRAM_MINI = 4;

/** The programs a client lets its device start, before the device's own support narrows them. */
export interface ClientDryingPrograms {
  selectablePrograms: number[];
  defaultProgram: number;
}

/**
 * Reads the client's programs for one device the way the client config dialog shows them. A Mini
 * without a Mini config of its own gets the Mini defaults: the Box config it falls back to carries
 * the Box's list and default program (Unterdruck on most clients), which a Mini does not run. No
 * config at all gets the Box or Mini defaults.
 *
 * @param clientConfig  The config the viewer's settings resolve to (for a Mini possibly the Box one).
 * @param isMini        Whether the device is a Mini.
 * @param isOwnMiniConfig Whether `clientConfig` is a saved Mini config, not the Box fallback.
 */
export function getClientDryingPrograms(
  clientConfig: ComBoxClientConfig | null,
  isMini: boolean,
  isOwnMiniConfig: boolean
): ClientDryingPrograms {
  if (isMini && !(clientConfig && isOwnMiniConfig)) {
    return {
      selectablePrograms: [...DEFAULT_SELECTABLE_DRYING_PROGRAMS_MINI],
      defaultProgram: DEFAULT_DRYING_PROGRAM_MINI,
    };
  }

  if (!clientConfig) {
    return {
      selectablePrograms: [...DEFAULT_SELECTABLE_DRYING_PROGRAMS_BOX],
      defaultProgram: DEFAULT_DRYING_PROGRAM_BOX,
    };
  }

  return {
    selectablePrograms: clientConfig.selectableDryingPrograms
      .map((isSelectable, program) => (isSelectable ? program : -1))
      .filter((program) => program >= 0),
    defaultProgram: clientConfig.defaultControlConfig.dryingProgram,
  };
}

/**
 * The program a new drying starts on: the preferred one when the list offers it, else the
 * client's default, else the first offered. A picker cannot show a program outside its list, so
 * preselecting one would start a program the user never saw (a Mini on Unterdruck).
 */
export function chooseStartDryingProgram(
  preferredProgram: number,
  defaultProgram: number,
  selectablePrograms: number[]
): number {
  if (selectablePrograms.includes(preferredProgram)) {
    return preferredProgram;
  }

  if (selectablePrograms.includes(defaultProgram)) {
    return defaultProgram;
  }

  return selectablePrograms[0] ?? preferredProgram;
}

/**
 * Configuration information for a ComBox client.
 */
export interface ComBoxClientConfigInfo {
  id: string;
  name: string;
  version: number;
  clientConfigVersion: number;
  firmwareVersion: string;
  humidityUnit: ComBoxHumidityUnit;
  dataBase64: string;
}

export interface ComBoxClientConfigContext {
  serialNumber?: string;
  isMini?: boolean;
}

function resolveComBoxClientConfigContext(
  context?: ComBoxClientConfigContext,
): ComBoxClientConfigContext {
  return context ?? {};
}

/**
 * Builds client config binary payload and metadata for Firestore.
 *
 * Use the named context object to control capability-dependent encoding.
 * - serialNumber: enables capability checks from device identity.
 * - isMini: explicit MINI/BOX override; takes precedence over serial inference.
 *
 * Positional boolean arguments are no longer supported.
 */
export function buildComBoxClientConfigInfo(
  clientConfig: ComBoxClientConfig,
  firmwareVersion: string,
  context?: ComBoxClientConfigContext,
): ComBoxClientConfigInfo {
  const { serialNumber = "", isMini } = resolveComBoxClientConfigContext(
    context,
  );

  const builder = new IresDeviceFrameBuilder(0);

  if (hasCapabilityForVersion(serialNumber, firmwareVersion, DeviceCapability.DRYING_CONFIG_NAME, isMini)) {
    builder.addString(clientConfig.name, 8);
  } else {
    builder.addUint8(clientConfig.version);
    builder.addString(clientConfig.name, 8);
  }

  builder.addUint8(
    clientConfig.humidityUnit === ComBoxHumidityUnit.RELATIVE_HUMIDITY
      ? 0
      : clientConfig.humidityUnit == ComBoxHumidityUnit.ABSOLUTE_HUMIDITY
        ? 1
        : 2
  );

  builder.addUint8(0);
  builder.addString(clientConfig.displayMessage, 24);

  const controlConfig = clientConfig.defaultControlConfig;

  builder.addUint8(controlConfig.socket1Mode + controlConfig.socket2Mode * 4);

  // Not used
  builder.addUint8(controlConfig.dryingMode);

  builder.addBitField(clientConfig.selectableDryingPrograms);
  
  // bit 0 set => hide MIXING_RATIO
  // bit 1 set => hide ABSOLUTE_HUMIDITY
  const hiddenHumidityBits = new Array(8).fill(false);
  if (clientConfig.hiddenHumidityUnit === ComBoxHumidityUnit.MIXING_RATIO) {
    hiddenHumidityBits[0] = true;
  } else if (
    clientConfig.hiddenHumidityUnit === ComBoxHumidityUnit.ABSOLUTE_HUMIDITY
  ) {
    hiddenHumidityBits[1] = true;
  }
  builder.addBitField(hiddenHumidityBits);

  builder.addUint8(controlConfig.dryingProgram);
  builder.addUint8(controlConfig.sensorProgram);

  if (hasCapabilityForVersion(serialNumber, firmwareVersion, DeviceCapability.SENSOR_USAGE_INFO, isMini)) {
    let toAdd = 0;

    /**
     * @deprecated
     * This is deprecated and not used by display.
     */
    controlConfig.sensorUsage.slice(0, 4).forEach((usage, index) => {
      let flag = 0;

      if (usage === ComBoxSensorUsage.MEASUREMENT) {
        flag = 1;
      } else if (usage === ComBoxSensorUsage.PROGRAM) {
        flag = 2;
      } else if (usage === ComBoxSensorUsage.REFERENCE) {
        flag = 3;
      }

      toAdd += flag << (index * 2);
    });

    builder.addUint8(toAdd);
  } else {
    builder.addBitField(
      controlConfig.sensorUsage.map(
        (usage) => usage == ComBoxSensorUsage.PROGRAM
      )
    );
  }

  builder.addUint8(
    (controlConfig.nightModeActive ? 1 : 0) +
      (controlConfig.nightModeAffectsAllSockets ? 1 : 0) * 2
  );

  builder.addUint8(controlConfig.nightModeStartHour);
  builder.addUint8(controlConfig.nightModeStartMinute);

  builder.addUint8(controlConfig.nightModeEndHour);
  builder.addUint8(controlConfig.nightModeEndMinute);

  builder.addBitField(controlConfig.nightModeActiveDays);
  builder.addBoolean(clientConfig.expertModeEnabled);

  return {
    id: generateRandomUuid(),
    firmwareVersion: firmwareVersion,
    clientConfigVersion: 1,
    humidityUnit: clientConfig.humidityUnit,
    version: clientConfig.version,
    name: clientConfig.name,
    dataBase64: base64Buffer.encode(builder.rawPayload),
  };
}

/**
 * Parses a stored client config payload into a ComBoxClientConfig model.
 *
 * Use the named context object to control capability-dependent decoding.
 * - serialNumber: enables capability checks from device identity.
 * - isMini: explicit MINI/BOX override; takes precedence over serial inference.
 *
 * Positional boolean arguments are no longer supported.
 */
export function getComBoxClientConfig(
  comBoxClientConfigInfo: ComBoxClientConfigInfo,
  context?: ComBoxClientConfigContext,
): ComBoxClientConfig {
  const { serialNumber = "", isMini } = resolveComBoxClientConfigContext(
    context,
  );

  const iresDeviceResponseReader = new IresDeviceResponseReader(
    new Uint8Array(base64Buffer.decode(comBoxClientConfigInfo.dataBase64))
  );

  let version = 0;
  let name = "";

  if (
    hasCapabilityForVersion(
      serialNumber,
      comBoxClientConfigInfo.firmwareVersion,
      DeviceCapability.DRYING_CONFIG_NAME,
      isMini,
    )
  ) {
    name = iresDeviceResponseReader.readString(8);
    version = 0;
  } else {
    version = iresDeviceResponseReader.readUint8();
    name = iresDeviceResponseReader.readString(8);
  }

  const displayConfig = iresDeviceResponseReader.readUint8();
  const humidityUnitFlag = displayConfig % 4;
  const humidityUnit =
    humidityUnitFlag === 1
      ? ComBoxHumidityUnit.ABSOLUTE_HUMIDITY
      : humidityUnitFlag === 2
        ? ComBoxHumidityUnit.MIXING_RATIO
        : ComBoxHumidityUnit.RELATIVE_HUMIDITY;

  iresDeviceResponseReader.skip(1);

  const displayMessage = iresDeviceResponseReader.readString(24);

  const socketMask = iresDeviceResponseReader.readUint8();

  const socket1Mode = getSocketModeFromFlag(socketMask % 4);
  const socket2Mode = getSocketModeFromFlag((socketMask >>> 2) % 4);
  const dryingMode = getDryingModeFromFlag(
    iresDeviceResponseReader.readUint8()
  );

  const selectableDryingPrograms = iresDeviceResponseReader.readBitField(8);

  // Bitfield map:
  // bit 0 set => hide MIXING_RATIO
  // but 1 set => hide ABSOLUTE_HUMIDITY
  const selectableSensorPrograms = iresDeviceResponseReader.readBitField(8);

  const isMixingRatioHidden = selectableSensorPrograms[0] === true;
  const isAbsoluteHumidityHidden = selectableSensorPrograms[1] === true; 

  const dryingProgram = iresDeviceResponseReader.readUint8();
  const sensorProgram = iresDeviceResponseReader.readUint8();

  const selectedSensors = iresDeviceResponseReader.readBitField(5);

  const nightModeFlags = iresDeviceResponseReader.readUint8();
  const nightModeActive = nightModeFlags % 2 !== 0;

  const nightModeAffectsAllSockets = (nightModeFlags >>> 1) % 2 !== 0;
  // Night pause hours in CLIENT_CONFIG are stored in local time (no UTC offset applied).
  // This differs from CONTROL_CONFIG, where night pause hours are expected in UTC.
  const nightModeStartHourWithoutUtcOffset = iresDeviceResponseReader.readUint8();
  const nightModeStartHour = nightModeStartHourWithoutUtcOffset % 24;

  const nightModeStartMinute = iresDeviceResponseReader.readUint8();

  const nightModeEndHourWithoutUtcOffset = iresDeviceResponseReader.readUint8();
  const nightModeEndHour = nightModeEndHourWithoutUtcOffset % 24;

  const nightModeEndMinute = iresDeviceResponseReader.readUint8();
  const nightModeActiveDays = iresDeviceResponseReader.readBitField(7);

  const controlConfig: ControlConfig = {
    socket1Mode: socket1Mode,
    socket2Mode: socket2Mode,
    dryingMode: dryingMode,
    dryingProgram: dryingProgram,
    sensorProgram: sensorProgram,
    sensorUsage: selectedSensors.map((isSelected, index) =>
      isSelected
        ? ComBoxSensorUsage.PROGRAM
        : index == 4
          ? ComBoxSensorUsage.REFERENCE
          : ComBoxSensorUsage.MEASUREMENT
    ),
    nightModeActive: nightModeActive,
    nightModeAffectsAllSockets: nightModeAffectsAllSockets,
    nightModeStartHour: nightModeStartHour,
    nightModeStartMinute: nightModeStartMinute,
    nightModeEndHour: nightModeEndHour,
    nightModeEndMinute: nightModeEndMinute,
    nightModeActiveDays: nightModeActiveDays,
  };

  const expertModeEnabled = iresDeviceResponseReader.readBoolean();

  /**
   * ==================== FIRMWARE v4.0.0+ CHANGES ====================
   * For firmware ≥ 4.0.0, read 5 additional bytes appended at the end.
   * - First byte: bytesFollowing (expected value = 4) identifying modified LTV record.
   * - Next 4 bytes: Contain encoded sensor usage bits.
   * - Decodes 8 sensors first (indices 0–3, 5–8).
   * - Room sensor info is parsed last and placed at index 4.
   * - Keeps backward compatibility with older firmware data.
   */
  if (hasCapabilityForVersion(serialNumber, comBoxClientConfigInfo.firmwareVersion, DeviceCapability.EXTENDED_SENSORS, isMini)) {
    const bytesFollowing = iresDeviceResponseReader.readUint8(); // should be 4

    if (bytesFollowing === 4) {
      let bitField = 0;
      for (let i = 0; i < 4; i++) {
        bitField |= iresDeviceResponseReader.readUint8() << (i * 8);
      }

      const decodeFlag = (flag: number): ComBoxSensorUsage => {
        if (flag === 1) return ComBoxSensorUsage.MEASUREMENT;
        if (flag === 2) return ComBoxSensorUsage.PROGRAM;
        if (flag === 3) return ComBoxSensorUsage.REFERENCE;
        return ComBoxSensorUsage.NOT_USED;
      };

      const sensorUsage: ComBoxSensorUsage[] = [];
      for (let i = 0; i < 8; i++) {
        const flag = bitField % 4;
        sensorUsage[i >= 4 ? i + 1 : i] = decodeFlag(flag);
        bitField = bitField >>> 2;
      }

      const roomFlag = bitField % 4;
      sensorUsage[4] = decodeFlag(roomFlag);

      controlConfig.sensorUsage = sensorUsage;
    }
  }

  return filterOutUndefined({
    version: version,
    humidityUnit: humidityUnit,
    name: name,
    expertModeEnabled: expertModeEnabled,
    displayMessage: displayMessage,
    selectableDryingPrograms: selectableDryingPrograms,
    selectableSensorPrograms: selectableSensorPrograms,
    defaultControlConfig: controlConfig,
    hiddenHumidityUnit: isMixingRatioHidden
      ? ComBoxHumidityUnit.MIXING_RATIO
      : isAbsoluteHumidityHidden
        ? ComBoxHumidityUnit.ABSOLUTE_HUMIDITY
        : undefined
  });
}
